/**
 * SalesTab Component
 * Year-specific sales data entry form for organizations
 */
import React, { memo, useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Save, Loader2, CalendarIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { format, parse, isValid } from 'date-fns';
import { SelectField, MultiCheckboxField, FIELD_OPTIONS } from './FieldOptions';

// Helper function to format date as DD-MMM-YYYY
const formatDateDisplay = (dateValue) => {
  if (!dateValue) return '';
  
  // If already a Date object
  if (dateValue instanceof Date && isValid(dateValue)) {
    return format(dateValue, 'dd-MMM-yyyy');
  }
  
  // Handle Excel serial numbers (number between 1 and 100000)
  const numValue = Number(dateValue);
  if (!isNaN(numValue) && numValue > 0 && numValue < 100000 && Number.isInteger(numValue)) {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
    if (isValid(date)) {
      return format(date, 'dd-MMM-yyyy');
    }
  }
  
  // If string, try to parse various formats
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    if (!trimmed) return '';
    
    // Try parsing various date formats
    const formats = [
      'yyyy-MM-dd',      // ISO
      'dd-MMM-yyyy',     // Our target format
      'dd/MM/yyyy',      // AU format
      'MM/dd/yyyy',      // US format
      'd-MMM-yyyy',      // Single digit day
      'dd MMM yyyy',     // Space separated
    ];
    
    for (const fmt of formats) {
      try {
        const parsed = parse(trimmed, fmt, new Date());
        if (isValid(parsed)) {
          return format(parsed, 'dd-MMM-yyyy');
        }
      } catch (e) {
        continue;
      }
    }
    
    // If ISO string with time
    try {
      const isoDate = new Date(trimmed);
      if (isValid(isoDate)) {
        return format(isoDate, 'dd-MMM-yyyy');
      }
    } catch (e) {}
    
    return trimmed; // Return original if can't parse
  }
  
  return '';
};

// Parse date string to Date object for calendar
const parseDateForCalendar = (dateValue) => {
  if (!dateValue) return undefined;
  
  if (dateValue instanceof Date && isValid(dateValue)) {
    return dateValue;
  }
  
  // Handle Excel serial numbers
  const numValue = Number(dateValue);
  if (!isNaN(numValue) && numValue > 0 && numValue < 100000 && Number.isInteger(numValue)) {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
    if (isValid(date)) return date;
  }
  
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    if (!trimmed) return undefined;
    
    const formats = [
      'yyyy-MM-dd',
      'dd-MMM-yyyy',
      'dd/MM/yyyy',
      'MM/dd/yyyy',
      'd-MMM-yyyy',
      'dd MMM yyyy',
    ];
    
    for (const fmt of formats) {
      try {
        const parsed = parse(trimmed, fmt, new Date());
        if (isValid(parsed)) {
          return parsed;
        }
      } catch (e) {
        continue;
      }
    }
    
    try {
      const isoDate = new Date(trimmed);
      if (isValid(isoDate)) {
        return isoDate;
      }
    } catch (e) {}
  }
  
  return undefined;
};

// Known date fields that should always display as DD-MMM-YYYY
const DATE_FIELD_NAMES = new Set([
  'job_confirmed', 'confirmation_letter_sent', 'date_booked',
  'photography_date_1', 'photography_date_2', 'photography_date_3',
  'photography_date_4', 'photography_date_5',
  'sample_sent', 'term_call', 'appointment_date',
  'date_downloaded', 'date_due_out', 'date_envelopes_sent',
  'date_job_assigned', 'date_job_packed', 'date_job_printed',
  'date_names_typed', 'date_ripped', 'dispatch_date',
  'job_sheet_printed', 'claim_form_received', 'commission_sent_date',
  'photographer_day_1', 'photographer_day_2', 'photographer_day_3',
  'photographer_day_4', 'photographer_day_5', 'photographer_day_6',
]);

// Optimized Sales field component - uses uncontrolled input to prevent re-renders
// Auto-formats known date fields to DD-MMM-YYYY
export const SalesField = memo(({ label, field, defaultValue, onFieldChange }) => {
  const isDateType = DATE_FIELD_NAMES.has(field);
  const displayVal = isDateType ? (formatDateDisplay(defaultValue) || defaultValue || '') : (defaultValue || '');
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-muted/50 last:border-0">
      <span className="text-sm text-muted-foreground w-44 shrink-0">{label}:</span>
      <input
        className="flex h-7 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        defaultValue={displayVal}
        onChange={(e) => onFieldChange(field, e.target.value)}
        placeholder="-"
      />
    </div>
  );
});

SalesField.displayName = 'SalesField';

// Date field with calendar picker - stores as DD-MMM-YYYY
export const DateField = memo(({ label, field, defaultValue, onFieldChange }) => {
  const [open, setOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState(formatDateDisplay(defaultValue));
  const inputRef = useRef(null);
  
  const selectedDate = parseDateForCalendar(displayValue || defaultValue);
  
  const handleDateSelect = (date) => {
    if (date && isValid(date)) {
      const formatted = format(date, 'dd-MMM-yyyy');
      setDisplayValue(formatted);
      onFieldChange(field, formatted);
      if (inputRef.current) {
        inputRef.current.value = formatted;
      }
    }
    setOpen(false);
  };
  
  const handleInputChange = (e) => {
    const value = e.target.value;
    setDisplayValue(value);
    // Format and save when user types
    const formatted = formatDateDisplay(value);
    onFieldChange(field, formatted || value);
  };
  
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-muted/50 last:border-0">
      <span className="text-sm text-muted-foreground w-44 shrink-0">{label}:</span>
      <div className="flex w-full gap-1">
        <input
          ref={inputRef}
          className="flex h-7 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          defaultValue={formatDateDisplay(defaultValue)}
          onChange={handleInputChange}
          placeholder="DD-MMM-YYYY"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="h-7 w-7 shrink-0">
              <CalendarIcon className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
});

DateField.displayName = 'DateField';

export const SalesTab = memo(({ 
  salesYear,
  salesData, 
  salesLoading, 
  salesDirty, 
  savingSales, 
  saveSalesData, 
  updateSalesFormRef 
}) => {
  // Customer History dropdown must always include the ACTIVE sales year
  // + every previous year down to 2011. `CUSTOMER_HISTORY_OPTIONS` in
  // FieldOptions.jsx is a static "up to 2026" list — good for the common
  // case but wrong when the operator picks 2027/2028 (they'd never see
  // the newer years as tick-boxes). Recompute per-render based on
  // `salesYear` so the dropdown always shows the active year + all
  // previous years.
  //
  // NOTE: this hook MUST live above the early return below (Rules of
  // Hooks — same order every render).
  const customerHistoryOptions = React.useMemo(() => {
    const activeYear = Math.max(Number(salesYear) || new Date().getFullYear(), new Date().getFullYear());
    const lowest = 2011;
    const years = [];
    for (let y = activeYear; y >= lowest; y--) years.push(String(y));
    return years;
  }, [salesYear]);

  if (salesLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" key={`sales-${salesData.org_id || 'unknown'}-${salesYear}-${salesData.sales_id || 'new'}`}>
      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          size="sm" 
          onClick={saveSalesData}
          disabled={!salesDirty || savingSales}
          className={cn(salesDirty && "animate-pulse")}
        >
          {savingSales ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          {salesDirty ? 'Save Changes' : 'Saved'}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Section 1 - Marketing & Booking */}
        <div className="space-y-1 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg p-3">
          <h5 className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2 uppercase tracking-wide">Marketing & Booking</h5>
          <SelectField label="Marketing Status" field="marketing_status" defaultValue={salesData.marketing_status} onFieldChange={updateSalesFormRef} />
          <DateField label="Sample Sent" field="sample_sent" defaultValue={salesData.sample_sent} onFieldChange={updateSalesFormRef} />
          <SalesField label="Sample Type Sent" field="sample_type_sent" defaultValue={salesData.sample_type_sent} onFieldChange={updateSalesFormRef} />
          <MultiCheckboxField label="Customer History" field="customer_history" defaultValue={salesData.customer_history} options={customerHistoryOptions} onFieldChange={updateSalesFormRef} />
          <SelectField label="New or Existing" field="new_or_existing" defaultValue={salesData.new_or_existing} onFieldChange={updateSalesFormRef} />
          <SalesField label="Sales Estimate" field="sales_estimate" defaultValue={salesData.sales_estimate} onFieldChange={updateSalesFormRef} />
          <SalesField label="Reasons for Loss" field="reasons_for_loss" defaultValue={salesData.reasons_for_loss} onFieldChange={updateSalesFormRef} />
          <DateField label="Appointment Date" field="appointment_date" defaultValue={salesData.appointment_date} onFieldChange={updateSalesFormRef} />
          <SalesField label="Appointment Time" field="appointment_time" defaultValue={salesData.appointment_time} onFieldChange={updateSalesFormRef} />
          <DateField label="Date Booked" field="date_booked" defaultValue={salesData.date_booked} onFieldChange={updateSalesFormRef} />
          <SalesField label="Booked By" field="booked_by" defaultValue={salesData.booked_by} onFieldChange={updateSalesFormRef} />
          <MultiCheckboxField label="Package Selected" field="package_selected" defaultValue={salesData.package_selected} options={FIELD_OPTIONS.package_selected} onFieldChange={updateSalesFormRef} />
          <MultiCheckboxField label="Envelope" field="envelope" defaultValue={salesData.envelope} options={FIELD_OPTIONS.envelope} onFieldChange={updateSalesFormRef} />
          <SalesField label="Special Envelope Inst" field="special_envelope_instructions" defaultValue={salesData.special_envelope_instructions} onFieldChange={updateSalesFormRef} />
          <SalesField label="Online URL" field="online_url" defaultValue={salesData.online_url} onFieldChange={updateSalesFormRef} />
          {/* Online Only Checkbox and Online Password */}
          <div className="flex items-center justify-between py-1.5 border-b border-muted/50 last:border-0 gap-4">
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-muted-foreground">Online Only:</span>
              <input
                type="checkbox"
                id="online-only-sales"
                defaultChecked={salesData.online_only === 'true' || salesData.online_only === true}
                onChange={(e) => {
                  updateSalesFormRef('online_only', e.target.checked ? 'true' : 'false');
                }}
                className="w-4 h-4 rounded cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm text-muted-foreground shrink-0">Online Password:</span>
              <input
                className="flex h-7 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                defaultValue={salesData.online_password || ''}
                onChange={(e) => updateSalesFormRef('online_password', e.target.value)}
                placeholder="-"
              />
            </div>
          </div>
        </div>

        {/* Section 2 - Photography Details */}
        <div className="space-y-1 bg-green-50/50 dark:bg-green-950/20 rounded-lg p-3">
          <h5 className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2 uppercase tracking-wide">Photography Details</h5>
          <SalesField label="Group Type" field="group_type" defaultValue={salesData.group_type} onFieldChange={updateSalesFormRef} />
          <SalesField label="Promotions & Specials" field="promotions_specials" defaultValue={salesData.promotions_specials} onFieldChange={updateSalesFormRef} />
          <SelectField label="Payment System" field="payment_system" defaultValue={salesData.payment_system} onFieldChange={updateSalesFormRef} />
          <SalesField label="# Enrolled Students" field="number_of_enrolled_students" defaultValue={salesData.number_of_enrolled_students} onFieldChange={updateSalesFormRef} />
          <SalesField label="Number of Groups" field="number_of_groups" defaultValue={salesData.number_of_groups} onFieldChange={updateSalesFormRef} />
          <SalesField label="Largest Group" field="largest_group" defaultValue={salesData.largest_group} onFieldChange={updateSalesFormRef} />
          <SalesField label="Photography # of Days" field="photography_days" defaultValue={salesData.photography_days} onFieldChange={updateSalesFormRef} />
          <DateField label="Photography Date 1" field="photography_date_1" defaultValue={salesData.photography_date_1} onFieldChange={updateSalesFormRef} />
          <DateField label="Photography Date 2" field="photography_date_2" defaultValue={salesData.photography_date_2} onFieldChange={updateSalesFormRef} />
          <DateField label="Photography Date 3" field="photography_date_3" defaultValue={salesData.photography_date_3} onFieldChange={updateSalesFormRef} />
          <DateField label="Photography Date 4" field="photography_date_4" defaultValue={salesData.photography_date_4} onFieldChange={updateSalesFormRef} />
          <DateField label="Photography Date 5" field="photography_date_5" defaultValue={salesData.photography_date_5} onFieldChange={updateSalesFormRef} />
          <SalesField label="Photography Start Time" field="photography_start_time" defaultValue={salesData.photography_start_time} onFieldChange={updateSalesFormRef} />
          <SalesField label="Second Day" field="second_day" defaultValue={salesData.second_day} onFieldChange={updateSalesFormRef} />
        </div>

        {/* Section 3 - Venue & ID Cards */}
        <div className="space-y-1 bg-amber-50/50 dark:bg-amber-950/20 rounded-lg p-3">
          <h5 className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 uppercase tracking-wide">Venue & ID Cards</h5>
          <MultiCheckboxField label="Indoor or Outdoor" field="indoor_or_outdoor" defaultValue={salesData.indoor_or_outdoor} options={FIELD_OPTIONS.indoor_or_outdoor} onFieldChange={updateSalesFormRef} />
          <SalesField label="Wet Weather Option" field="wet_weather_option" defaultValue={salesData.wet_weather_option} onFieldChange={updateSalesFormRef} />
          <SelectField label="Graduation" field="graduation" defaultValue={salesData.graduation} onFieldChange={updateSalesFormRef} />
          <SalesField label="Number of Graduates" field="number_of_graduates" defaultValue={salesData.number_of_graduates} onFieldChange={updateSalesFormRef} />
          <SelectField label="Gowns Required" field="gowns_required" defaultValue={salesData.gowns_required} onFieldChange={updateSalesFormRef} />
          <SalesField label="Vehicle Access" field="vehicle_access" defaultValue={salesData.vehicle_access} onFieldChange={updateSalesFormRef} />
          <SalesField label="Nearest Cross Street" field="nearest_cross_street" defaultValue={salesData.nearest_cross_street} onFieldChange={updateSalesFormRef} />
          <SalesField label="Parking Facilities" field="parking_facilities" defaultValue={salesData.parking_facilities} onFieldChange={updateSalesFormRef} />
          <SelectField label="School/Club to Proof" field="school_or_club_to_proof" defaultValue={salesData.school_or_club_to_proof} onFieldChange={updateSalesFormRef} />
          <SalesField label="Poster Details" field="poster_details" defaultValue={salesData.poster_details} onFieldChange={updateSalesFormRef} />
          <SelectField label="ID Card Type" field="id_card_type" defaultValue={salesData.id_card_type} onFieldChange={updateSalesFormRef} />
          <SelectField label="ID Cards" field="id_cards" defaultValue={salesData.id_cards} onFieldChange={updateSalesFormRef} />
          <SalesField label="ID Cards Info" field="id_cards_info" defaultValue={salesData.id_cards_info} onFieldChange={updateSalesFormRef} />
          <SalesField label="ID Cards Price" field="id_cards_price" defaultValue={salesData.id_cards_price} onFieldChange={updateSalesFormRef} />
          <SalesField label="Disc Requirements" field="disc_requirements" defaultValue={salesData.disc_requirements} onFieldChange={updateSalesFormRef} />
        </div>

        {/* Section 4 - Extras & Follow-ups */}
        <div className="space-y-1 bg-purple-50/50 dark:bg-purple-950/20 rounded-lg p-3">
          <h5 className="text-xs font-semibold text-purple-700 dark:text-purple-400 mb-2 uppercase tracking-wide">Extras & Follow-ups</h5>
          <SelectField label="Muck Up Photos" field="muck_up_photos" defaultValue={salesData.muck_up_photos} onFieldChange={updateSalesFormRef} />
          <SelectField label="Staff Photos" field="staff_photos" defaultValue={salesData.staff_photos} onFieldChange={updateSalesFormRef} />
          <SelectField label="Around School Photos" field="around_school_photos" defaultValue={salesData.around_school_photos} onFieldChange={updateSalesFormRef} />
          <SelectField label="Whole School Photo" field="whole_school_photo" defaultValue={salesData.whole_school_photo} onFieldChange={updateSalesFormRef} />
          <SalesField label="Extra Curricular Groups" field="extra_curricular_groups" defaultValue={salesData.extra_curricular_groups} onFieldChange={updateSalesFormRef} />
          <SalesField label="Principal" field="principal" defaultValue={salesData.principal} onFieldChange={updateSalesFormRef} />
          <SalesField label="Extras" field="extras" defaultValue={salesData.extras} onFieldChange={updateSalesFormRef} />
          <SalesField label="Category" field="category" defaultValue={salesData.category} onFieldChange={updateSalesFormRef} />
          <SelectField label="Commission Structure" field="commission_structure" defaultValue={salesData.commission_structure} onFieldChange={updateSalesFormRef} />
          <DateField label="Confirm Letter Sent" field="confirmation_letter_sent" defaultValue={salesData.confirmation_letter_sent} onFieldChange={updateSalesFormRef} />
          <SalesField label="Follow Up 1" field="follow_up_1" defaultValue={salesData.follow_up_1} onFieldChange={updateSalesFormRef} />
          <SalesField label="Follow Up 2" field="follow_up_2" defaultValue={salesData.follow_up_2} onFieldChange={updateSalesFormRef} />
          <DateField label="Term Call" field="term_call" defaultValue={salesData.term_call} onFieldChange={updateSalesFormRef} />
        </div>
      </div>
    </div>
  );
});

SalesTab.displayName = 'SalesTab';

export default SalesTab;
